import Image from "next/image";

import { cn } from "@/lib/utils";

/** Figma Screen (10927:78469) — native 360×232 layout inside NavFloatingCard. */
const CARD_LEFT = 12;
const CARD_TOP = 12;
const CARD_WIDTH = 336;
const CARD_HEIGHT = 145;
const CARD_INSET_X = 17.09;
const TAB_TOP = 176;
const TAB_INSET_X = 12;
const TAB_GAP = 9;

const CHART_TRACKS = [
  {
    src: "/images/nav/insights-screen/track-4.svg",
    left: 1.41,
    top: 20.51,
    width: 114.65,
    height: 45.804,
    inset: "inset-[0.46%_-0.49%_-1.23%_-0.49%]",
  },
  {
    src: "/images/nav/insights-screen/track-5.svg",
    left: 116.06,
    top: 52.83,
    width: 34.845,
    height: 2.248,
    inset: "inset-[-25%_-1.61%]",
  },
  {
    src: "/images/nav/insights-screen/track-3.svg",
    left: 150.9,
    top: 52.83,
    width: 23.604,
    height: 10.116,
    inset: "inset-[-5.56%_-2.38%]",
  },
  {
    src: "/images/nav/insights-screen/track-6.svg",
    left: 174.51,
    top: 48.33,
    width: 27.539,
    height: 21.075,
    inset: "inset-[-2.67%_-2.04%_-1.54%_-2.04%]",
  },
  {
    src: "/images/nav/insights-screen/track-2.svg",
    left: 202.04,
    top: 48.33,
    width: 29.225,
    height: 34.002,
    inset: "inset-[-1.65%_-1.92%]",
  },
  {
    src: "/images/nav/insights-screen/track-7.svg",
    left: 231.27,
    top: 67.72,
    width: 28.944,
    height: 16.089,
    inset: "inset-[-1.1%_-1.94%_-3.49%_-1.94%]",
  },
  {
    src: "/images/nav/insights-screen/track-1.svg",
    left: 260.21,
    top: 83.8,
    width: 28.101,
    height: 6.392,
    inset: "inset-[-8.79%_-2%]",
  },
  {
    src: "/images/nav/insights-screen/track-8.svg",
    left: 288.31,
    top: 90.2,
    width: 29.506,
    height: 14.05,
    inset: "inset-[-4%_-1.91%]",
  },
] as const;

/** Chart geometry is authored for a 370px card — scale to our 336px card width. */
const CHART_SCALE = CARD_WIDTH / 370;

type TabKey = "research" | "data" | "analysis";

const TAB_CONFIG: {
  key: TabKey;
  icon: string;
  active?: boolean;
}[] = [
  {
    key: "research",
    icon: "/images/nav/insights-screen/icon-folder-search.svg",
    active: true,
  },
  {
    key: "data",
    icon: "/images/nav/insights-screen/icon-apple-wallet.svg",
  },
  {
    key: "analysis",
    icon: "/images/nav/insights-screen/icon-graph-up.svg",
  },
];

export type InsightsNavScreenCopy = {
  availableBalance: string;
  netFlow: string;
  netFlowChange: string;
  interestEarned: string;
  interestAmount: string;
  balance: string;
  allTime: string;
  tooltipDate: string;
  tooltipAmount: string;
  axisStart: string;
  axisEnd: string;
  tabs: Record<TabKey, string>;
};

type InsightsNavScreenProps = {
  copy: InsightsNavScreenCopy;
  className?: string;
};

/** Static HTML recreation of Figma Screen (10927:78469). */
export function InsightsNavScreen({ copy, className }: InsightsNavScreenProps) {
  return (
    <div
      className={cn(
        "rounded-tl-ds-xl pointer-events-none relative h-full w-full cursor-default overflow-hidden bg-white",
        className
      )}
    >
      <BalanceCard copy={copy} />
      <NavTabs copy={copy} />
    </div>
  );
}

function BalanceCard({ copy }: { copy: InsightsNavScreenCopy }) {
  return (
    <div
      aria-hidden
      className="border-grey-100 absolute overflow-hidden rounded-[9px] border bg-white"
      style={{
        left: CARD_LEFT,
        top: CARD_TOP,
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
      }}
    >
      <p className="text-grey-950 absolute top-[20px] left-[17px] text-[10px] leading-none font-medium whitespace-nowrap">
        {copy.availableBalance}
      </p>

      <div className="absolute top-[36px] left-[17px] flex items-start gap-3">
        <TrendStat
          label={copy.netFlow}
          value={
            <>
              <span className="text-[6px]">↑</span>
              <span className="text-green-600">{copy.netFlowChange}</span>
            </>
          }
          valueClassName="text-green-600"
        />
        <TrendStat
          label={
            <>
              {copy.interestEarned}
              <sup className="text-[5px]">1</sup>
            </>
          }
          value={copy.interestAmount}
          valueClassName="text-grey-950"
        />
      </div>

      <p className="text-grey-950 absolute top-[62px] left-[17px] -translate-y-1/2 text-[12.5px] leading-none font-medium tracking-[-0.26px] whitespace-nowrap">
        {copy.balance}
      </p>

      <div
        aria-hidden
        className="text-grey-950 border-grey-100 absolute top-[19px] right-[10px] flex h-[18px] items-center gap-0.5 rounded-full border bg-white px-1.5 text-[7px] leading-none"
      >
        {copy.allTime}
        <ChevronDownIcon />
      </div>

      <ChartArea copy={copy} />
    </div>
  );
}

function TrendStat({
  label,
  value,
  valueClassName,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center gap-1 whitespace-nowrap">
      <span className="text-grey-600 text-[7px] leading-none">{label}</span>
      <span
        className={cn(
          "flex items-center gap-0.5 text-[7px] leading-none",
          valueClassName
        )}
      >
        {value}
      </span>
    </div>
  );
}

function ChartArea({ copy }: { copy: InsightsNavScreenCopy }) {
  const chartLeft = CARD_INSET_X * CHART_SCALE;
  const chartWidth = 343.951 * CHART_SCALE;

  return (
    <>
      <div
        className="absolute flex items-end"
        style={{
          left: chartLeft,
          top: 80,
          width: chartWidth,
          bottom: -24,
        }}
      >
        <div className="flex h-full min-w-0 flex-1 flex-col items-start justify-end gap-2">
          <div
            className="relative w-0 shrink-0"
            style={{ height: 70 * CHART_SCALE }}
          >
            <Image
              src="/images/nav/insights-screen/chart-wrapper.svg"
              alt=""
              width={1}
              height={70}
              className="absolute inset-[0_-0.56px_0_0] h-full w-auto max-w-none"
              aria-hidden
            />
          </div>
          <div className="text-grey-600 flex w-full shrink-0 justify-between text-center text-[7px] leading-none">
            <span>{copy.axisStart}</span>
            <span>{copy.axisEnd}</span>
          </div>
        </div>
      </div>

      <div
        className="absolute"
        style={{
          left: chartLeft,
          top: 60,
          width: 343.389 * CHART_SCALE,
          height: 60,
        }}
      >
        {CHART_TRACKS.map((track) => (
          <div
            key={track.src}
            aria-hidden
            className="absolute"
            style={{
              left: track.left * CHART_SCALE,
              top: track.top * CHART_SCALE,
              width: track.width * CHART_SCALE,
              height: track.height * CHART_SCALE,
            }}
          >
            <div className={cn("absolute", track.inset)}>
              <img
                src={track.src}
                alt=""
                className="block size-full max-w-none"
              />
            </div>
          </div>
        ))}

        <div
          aria-hidden
          className="absolute size-[3px]"
          style={{
            left: 114.09 * CHART_SCALE,
            top: 52.26 * CHART_SCALE,
          }}
        >
          <div className="absolute inset-[-33.33%]">
            <img
              src="/images/nav/insights-screen/chart-dot.svg"
              alt=""
              className="block size-full max-w-none"
            />
          </div>
        </div>

        <ChartTooltip copy={copy} />
      </div>
    </>
  );
}

/** Figma tooltip (10927:78488) — fixed-height rows for date/amount separation. */
function ChartTooltip({ copy }: { copy: InsightsNavScreenCopy }) {
  return (
    <div
      className="absolute flex flex-col rounded-[4.5px] bg-black p-[2px] shadow-[0px_2px_0.5px_-1px_rgba(0,0,0,0.04)]"
      style={{
        left: 119.71 * CHART_SCALE,
        top: 42.15 * CHART_SCALE,
      }}
    >
      <div className="flex h-[11px] items-center px-[2px]">
        <p className="text-[6.5px] leading-none whitespace-nowrap text-[#cfcfcf]">
          {copy.tooltipDate}
        </p>
      </div>
      <div className="flex h-[11px] items-center px-[2px]">
        <p className="text-grey-0 text-[7.5px] leading-none font-medium whitespace-nowrap">
          {copy.tooltipAmount}
        </p>
      </div>
    </div>
  );
}

function NavTabs({ copy }: { copy: InsightsNavScreenCopy }) {
  return (
    <div
      className="absolute flex items-center"
      style={{
        top: TAB_TOP,
        left: TAB_INSET_X,
        right: TAB_INSET_X,
        gap: TAB_GAP,
      }}
    >
      {TAB_CONFIG.map((tab) => (
        <div
          key={tab.key}
          className={cn(
            "bg-grey-0 flex min-w-0 flex-1 items-center gap-1.5 rounded-[10px] p-1 shadow-[6px_7px_11px_rgba(0,0,0,0.1)]",
            tab.active && "border-grey-100 border"
          )}
        >
          <div className="border-grey-100 bg-grey-0 relative size-6 shrink-0 overflow-hidden rounded-[6px] border">
            <Image
              src={tab.icon}
              alt=""
              width={14}
              height={14}
              className="absolute top-1 left-1"
              aria-hidden
            />
          </div>
          <p
            className={cn(
              "text-[10px] leading-[1.4] whitespace-nowrap",
              tab.active ? "text-grey-950" : "text-grey-400"
            )}
          >
            {copy.tabs[tab.key]}
          </p>
        </div>
      ))}
    </div>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 8 8" className="size-2 shrink-0" aria-hidden fill="none">
      <path
        d="M1.5 2.75L4 5.25L6.5 2.75"
        stroke="currentColor"
        strokeWidth="0.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
